import {
  Pagination,
  PaginationItem,
  PaginationRenderItemParams,
  Stack,
  Typography,
} from "@mui/material";
import { DEFAULT_PAGE_SIZE } from "commons/constants";
import {
  AutoCompleteSelectedValue,
  OptionItem,
} from "commons/ultilities/dynamic-form/models/control";
import { useCallback, useRef, useState } from "react";
import { XAutoComplete } from "./x-auto-complete";

export type PaginationSearchResult = {
  items?: OptionItem[];
  totalCount?: number;
};

export type PaginationSearchRequest = {
  pageIndex: number;
  pageSize: number;
};

export type XAutoCompleteWithPaginationProps = {
  onValueChanged?: (optionItem: OptionItem | null) => void;
  value?: OptionItem["value"] | null;
  isValid?: boolean;
  searchFunc: (
    keyword: string,
    request: PaginationSearchRequest,
  ) => Promise<PaginationSearchResult>;
  label?: string;
  helpText?: string;
  placeholder?: string;
  defaultOptions?: OptionItem[];
  searchOnFocus?: boolean;
  searchWhenRender?: boolean;
  pageSize?: number;
  resolveSelectedOptionAsync?: (
    value: AutoCompleteSelectedValue,
  ) => Promise<OptionItem | null>;
};

const PAGINATION_CONTROL_VALUE = "__x_auto_complete_pagination_control__";

export const XAutoCompleteWithPagination = ({
  onValueChanged,
  value,
  isValid = true,
  searchFunc,
  label = "",
  helpText = "",
  placeholder = "",
  defaultOptions,
  searchOnFocus = true,
  searchWhenRender = false,
  resolveSelectedOptionAsync,
  pageSize = DEFAULT_PAGE_SIZE,
}: XAutoCompleteWithPaginationProps) => {
  const pageIndexRef = useRef<number>(0);
  const keywordRef = useRef<string>("");
  const useCurrentKeywordForNextSearchRef = useRef<boolean>(false);

  const [forceSearchWhenRender, setForceSearchWhenRender] =
    useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [refreshToken, setRefreshToken] = useState<number>(0);

  const requestLoadPage = useCallback(
    (targetPageIndex: number) => {
      setDropdownOpen(true);
      pageIndexRef.current = targetPageIndex;
      useCurrentKeywordForNextSearchRef.current = true;

      if (!searchWhenRender) {
        setForceSearchWhenRender(true);
      }

      setRefreshToken((prev) => prev + 1);
    },
    [searchWhenRender],
  );

  const createPaginationOption = useCallback(
    (
      currentPageIndex: number,
      currentItemsCount: number,
      allCount?: number,
    ) => {
      const calculatedTotalPages =
        typeof allCount === "number" && allCount > 0
          ? Math.ceil(allCount / pageSize)
          : undefined;

      const hasPrev = currentPageIndex > 0;
      const hasNext =
        calculatedTotalPages != null
          ? currentPageIndex + 1 < calculatedTotalPages
          : currentItemsCount >= pageSize;

      const shouldShowPagination =
        hasPrev ||
        hasNext ||
        (calculatedTotalPages != null && calculatedTotalPages > 1);

      if (!shouldShowPagination) {
        return [] as OptionItem[];
      }

      const fallbackTotalPages = Math.max(
        currentPageIndex + 1 + (hasNext ? 1 : 0),
        1,
      );
      const paginationCount = calculatedTotalPages ?? fallbackTotalPages;
      const currentPage = currentPageIndex + 1;
      const pageInfoText = `${currentPage}/${paginationCount}`;

      const renderPaginationItem = (item: PaginationRenderItemParams) => {
        if (
          item.type === "page" ||
          item.type === "start-ellipsis" ||
          item.type === "end-ellipsis"
        ) {
          return <PaginationItem {...item} sx={{ display: "none" }} />;
        }

        return <PaginationItem {...item} />;
      };

      return [
        {
          value: PAGINATION_CONTROL_VALUE,
          label: pageInfoText,
          renderOption: (
            <Stack
              width={"100%"}
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              spacing={1}
              px={1}
              py={0.5}
            >
              <Pagination
                color="primary"
                size="small"
                shape="rounded"
                showFirstButton
                showLastButton
                page={currentPage}
                count={paginationCount}
                renderItem={renderPaginationItem}
                onChange={(_, selectedPage) => {
                  requestLoadPage(selectedPage - 1);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {pageInfoText}
              </Typography>
            </Stack>
          ),
        },
      ];
    },
    [pageSize, requestLoadPage],
  );

  const loadOptions = useCallback(
    async (keywordInput: string) => {
      // Keeps refreshToken in callback semantics so changing token recreates this function.
      const currentRefreshToken = refreshToken;
      void currentRefreshToken;

      const effectiveKeyword = useCurrentKeywordForNextSearchRef.current
        ? keywordRef.current
        : keywordInput;

      useCurrentKeywordForNextSearchRef.current = false;

      const keywordChanged = effectiveKeyword !== keywordRef.current;
      if (keywordChanged) {
        keywordRef.current = effectiveKeyword;

        if (pageIndexRef.current !== 0) {
          pageIndexRef.current = 0;
        }
      }

      const currentPageIndex = pageIndexRef.current;

      try {
        const result = await searchFunc(effectiveKeyword, {
          pageIndex: currentPageIndex,
          pageSize,
        });

        const pageItems = result?.items || [];

        const paginationOptions = createPaginationOption(
          currentPageIndex,
          pageItems.length,
          result?.totalCount,
        );

        return [...pageItems, ...paginationOptions];
      } finally {
        if (forceSearchWhenRender && !searchWhenRender) {
          setForceSearchWhenRender(false);
        }
      }
    },
    [
      createPaginationOption,
      forceSearchWhenRender,
      pageSize,
      refreshToken,
      searchFunc,
      searchWhenRender,
    ],
  );

  const onValueChangedHandler = useCallback(
    (selectedOption: OptionItem | null) => {
      if (selectedOption == null) {
        onValueChanged?.(null);
        return;
      }

      if (selectedOption.value === PAGINATION_CONTROL_VALUE) {
        setDropdownOpen(true);
        return;
      }

      onValueChanged?.(selectedOption);
      setDropdownOpen(false);
    },
    [onValueChanged],
  );

  const onOpenChangedHandler = useCallback((isOpen: boolean) => {
    setDropdownOpen(isOpen);
  }, []);

  const isOptionInteractionDisabledHandler = useCallback(
    (option: OptionItem) => {
      return option.value === PAGINATION_CONTROL_VALUE;
    },
    [],
  );

  const getOptionDisabledHandler = useCallback((option: OptionItem) => {
    return option.value === PAGINATION_CONTROL_VALUE;
  }, []);

  return (
    <XAutoComplete
      onValueChanged={onValueChangedHandler}
      value={value}
      isValid={isValid}
      searchFunc={loadOptions}
      label={label}
      helpText={helpText}
      placeholder={placeholder}
      defaultOptions={defaultOptions}
      searchOnFocus={searchOnFocus}
      searchWhenRender={searchWhenRender || forceSearchWhenRender}
      open={dropdownOpen}
      onOpenChanged={onOpenChangedHandler}
      disableCloseOnSelect={true}
      getOptionDisabled={getOptionDisabledHandler}
      isOptionInteractionDisabled={isOptionInteractionDisabledHandler}
      resolveSelectedOptionAsync={resolveSelectedOptionAsync}
    />
  );
};
